import React, { useEffect, useState, useRef, useContext, useMemo } from 'react'
import { useRouter } from 'next/router'
import { api } from '../../../utils'
import { PERSONALIZZAZIONI, PRODOTTI, PERSONALIZZAZIONICARRELLO } from '../../../utils/endpoint'
import TecnicaCard from '../../molecules/TecnicaCard/TecnicaCard'
import FormSelect from '../../atoms/FormSelect/FormSelect'
import useTrans from '../../../hooks/useTrans'
import MyImage from '../../atoms/MyImage/MyImage'
import FormRadio from '../../atoms/FormRadio/FormRadio'
import Button from '../../atoms/Button/Button'
import { Altezza, Clipboard, Customize, Larghezza, Zoom } from '../../atoms/NewIcons/index'
import FormField from '../../atoms/FormField/FormField'
import FormCheckbox from '../../atoms/FormCheckbox/FormCheckbox'
import FormInput from '../../atoms/FormInput/FormInput'
import { handleScrollRef, priceNotation } from '../../../utils/safe'
import NewModal from '../../molecules/NewModal/NewModal'
import * as Sentry from '@sentry/browser'
import { _uploadLoghiDimensioni } from '../UploadLoghi/UploadLoghi'
import ConfigurazioneProdottoConferma from '../ConfigurazioneProdottoConferma/ConfigurazioneProdottoConferma'
import Tooltip from '../../atoms/Tooltip/Tooltip'
import { displayNotification } from '../../atoms/Notifica/Notifica'
import ModalBlocks from '../../molecules/ModalBlocks/ModalBlocks'
import Link from 'next/link'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import * as yup from 'yup'
import { useFormik } from 'formik'
import {
  trackingAddCart,
  trackingCreaOfferta,
  trackingGAEventProdotto,
  trackingSalvaPerDopo,
} from '../../../utils/gtm'

const getMinPrice = (tecnica, prezziPersonalizzazioni) => {
  const matchedTecnica = prezziPersonalizzazioni?.tecniche?.find((t) => t.id === tecnica.id)

  let minPrice = null

  matchedTecnica?.posizioni?.forEach((posizione) => {
    posizione.numero_colori?.forEach((numeroColore) => {
      numeroColore.personalizzazioni_objects?.forEach((obj) => {
        const prezzo = obj.prezzi_personalizzazione?.[0]?.prezzo
        if (typeof prezzo === 'number' && (minPrice === null || prezzo < minPrice)) {
          minPrice = prezzo
        }
      })
    })
  })

  return minPrice
}

const getPrezzoUnitario = (prezziTecnica, quantita) => {
  const quantitaTotale =
    quantita && Array.isArray(quantita)
      ? quantita.reduce((acc, curr) => acc + (curr.quantita || 0), 0)
      : 0
  const prezzoItem =
    prezziTecnica?.posizioni?.[0]?.numero_colori?.[0]?.personalizzazioni_objects?.[0]
  if (!prezzoItem?.prezzi_personalizzazione || quantitaTotale === 0) return null
  const prezzi = prezzoItem.prezzi_personalizzazione
  const prezzo = prezzi
    .filter((p) => quantitaTotale >= p.a_partire_da)
    .sort((a, b) => b.a_partire_da - a.a_partire_da)[0]
  return prezzo ? prezzo.prezzo : prezzi[0].prezzo
}

const getCostoImpiantoUnitario = (prezziTecnica, quantita) => {
  const quantitaTotale =
    quantita && Array.isArray(quantita)
      ? quantita.reduce((acc, curr) => acc + (curr.quantita || 0), 0)
      : 0
  const prezzo = prezziTecnica?.costo_impianto || 0
  if (!prezzo || quantitaTotale === 0) return 0
  return prezzo / quantitaTotale
}

const trackFinalStep = (first_articolo, prodotto, selectedTecnica) => {
  const titolo = first_articolo?.titolo || ''
  const codice = prodotto?.codice || ''
  const tecnica = selectedTecnica?.nome || ''

  trackingGAEventProdotto(
    'customization_funnel',
    'c_step4_caricamento_loghi',
    undefined,
    titolo,
    codice,
    tecnica
  )

  trackingGAEventProdotto(
    'customization_funnel',
    'c_step5_nome_logo_OLD',
    undefined,
    titolo,
    codice,
    tecnica
  )

  trackingGAEventProdotto(
    'customization_complete',
    'c_step6_personalizzazione_confermata',
    undefined,
    titolo,
    codice,
    tecnica
  )
}

const checkIsColorNotAllowed = (color, allColors) =>
  allColors.findIndex(({ id }) => id === color) === -1

const ConfigurazioneProdotto = ({
  prodotto,
  personalizzazioni,
  prezziPersonalizzazioni,
  quantita,
  isLoading,
  setLoading,
  AddToCart,
  AddToCartMain,
  OrdinaSubito,
  SaveLater,
  resetCarrello,
  ModificaArticoli,
  setShowPersonalizzaForm,
  ordine,
  needRecomputePreventivo,
  setNeedRecomputePreventivo,
  doRecomputePreventivo,
  isAggiornaPreventivoEnabled,
  setAggiornaPreventivoEnabled,
  isAlreadyAggiornaPreventivoEnabled, // dopo la prima volta che viene aggiornato il preventivo diventa true
  setAlreadyAggiornaPreventivoEnabled,
  setDoRecomputePreventivo,
  variant,
  personalizzazioneId,
  initialValues,
  isFormValid,
  setFormValid,
}) => {
  const isEdit = variant === 'personalizzazione' && personalizzazioneId
  const router = useRouter()
  const t = useTrans()

  const selectedArticlesColors = useMemo(() => {
    const selectedArticlesIds = quantita.reduce(
      (acc, { id, quantita }) => (quantita > 0 ? [...acc, id] : acc),
      []
    )
    return (prodotto?.articoli ?? []).reduce((acc, { id, attributi_lista }) => {
      if (selectedArticlesIds.includes(id)) {
        const articleColorId = attributi_lista.find(
          ({ attributo_chiave }) => attributo_chiave === 'colore'
        ).id
        if (articleColorId && !acc.includes(articleColorId)) acc.push(articleColorId)
      }
      return acc
    }, [])
  }, [quantita, prodotto?.articoli])

  const tecniche = useMemo(() => {
    const tecnicheProdotto = personalizzazioni?.tecniche ?? []
    return tecnicheProdotto.filter(({ colori }) =>
      colori?.length > 0
        ? !selectedArticlesColors.some((color) => checkIsColorNotAllowed(color, colori))
        : true
    )
  }, [personalizzazioni, prezziPersonalizzazioni, quantita, selectedArticlesColors])

  const tecnicaEconomica = useMemo(() => {
    if (!personalizzazioni?.tecniche || !prezziPersonalizzazioni?.tecniche) return null
    let minPrezzo = null
    let min = null
    personalizzazioni.tecniche.forEach((tecnica) => {
      const prezziTecnica = prezziPersonalizzazioni.tecniche.find((t) => t.id === tecnica.id)
      const prezzoUnitario =
        getPrezzoUnitario(prezziTecnica, quantita) +
        getCostoImpiantoUnitario(prezziTecnica, quantita)
      if (
        typeof prezzoUnitario === 'number' &&
        (minPrezzo === null || prezzoUnitario < minPrezzo)
      ) {
        minPrezzo = prezzoUnitario
        min = tecnica
      }
    })
    return min
  }, [personalizzazioni, prezziPersonalizzazioni, quantita])

  const first_articolo = prodotto?.articoli.length > 0 ? prodotto?.articoli[0] : null
  const [selectedTecnica, setSelectedTecnica] = useState(
    initialValues ? initialValues?.tecnica : null
  )
  const [posizioni, setPosizioni] = useState(initialValues ? initialValues?.posizioni : [])
  const [nomeLavorazioneAssegnato, setNomeLavorazioneAssegnato] = useState(
    initialValues && initialValues?.nomeLavorazioneManuale ? initialValues?.nomeLavorazione : ''
  )
  const [showAnnullaConfigurazioneModal, setShowAnnullaConfigurazioneModal] = useState(false)
  const [showPrenotazioneModal, setShowPrenotazioneModal] = useState(false)
  const [confezionamentoRichiesto, setConfezionamentoRichiesto] = useState(
    initialValues?.confezionamento ? true : false
  )

  const validationSchema = yup.object({
    nomeLavorazione: yup
      .string()
      .matches(/^[^0-9]*$/, 'Solo lettere')
      .min(3, t('Minimo 3 caratteri'))
      .max(14, t('Massimo 14 caratteri')),
  })

  const formik = useFormik({
    initialValues: {
      nomeLavorazione:
        initialValues && initialValues?.nomeLavorazioneManuale
          ? initialValues?.nomeLavorazioneNoIncrementale
          : '',
    },
    validationSchema,
  })

  useEffect(() => {
    initialValues?.tecnica &&
      trackingGAEventProdotto(
        'customization_funnel',
        'c_step0_tecnica_stampa',
        undefined,
        first_articolo?.titolo || '',
        prodotto?.codice || '',
        initialValues?.tecnica?.nome || ''
      )
  }, [])

  useEffect(() => {
    if (selectedTecnica == initialValues?.tecnica) return
    if (selectedTecnica) {
      trackingGAEventProdotto(
        'customization_funnel',
        'c_step0_tecnica_stampa',
        undefined,
        first_articolo?.titolo || '',
        prodotto?.codice || '',
        selectedTecnica?.nome || ''
      )
      const primaPosizione = selectedTecnica.posizioni?.[0]
      if (primaPosizione) {
        setPosizioni([
          {
            ...primaPosizione,
            colori: primaPosizione.numero_colori?.[0],
            pantoni_codici: [],
            selectedAltezza:
              primaPosizione.numero_colori?.[0]?.personalizzazioni_objects[0].dimensione_altezza,
            selectedLarghezza:
              primaPosizione.numero_colori?.[0]?.personalizzazioni_objects[0].dimensione_larghezza,
          },
        ])
      } else {
        setPosizioni([])
      }
    } else {
      setPosizioni([])
    }
  }, [selectedTecnica])

  const handleAggiungiPosizione = () => {
    if (!selectedTecnica) return
    if (posizioni.length >= 2) return

    const nuovaPosizione = selectedTecnica.posizioni?.find(
      (p) => !posizioni.some((pos) => pos.id === p.id)
    )

    if (nuovaPosizione) {
      setPosizioni([
        ...posizioni,
        {
          ...nuovaPosizione,
          colori: nuovaPosizione.numero_colori?.[0],
          pantoni_codici: [],
          selectedAltezza:
            nuovaPosizione.numero_colori?.[0]?.personalizzazioni_objects[0].dimensione_altezza,
          selectedLarghezza:
            nuovaPosizione.numero_colori?.[0]?.personalizzazioni_objects[0].dimensione_larghezza,
        },
      ])
    }
  }

  const handleRimuoviPosizione = (index) => {
    if (posizioni.length <= 1) return
    setPosizioni((prev) => prev.filter((_, i) => i !== index))
  }

  const handleChangePosizione = (index, key, value) => {
    const updated = [...posizioni]
    if (key === 'posizione') {
      const nuovaPosizione = selectedTecnica.posizioni?.find((x) => x.id === value)
      if (nuovaPosizione) {
        updated[index] = {
          ...nuovaPosizione,
          colori: nuovaPosizione.numero_colori?.[0],
          pantoni_codici: [],
          selectedAltezza:
            nuovaPosizione.numero_colori?.[0]?.personalizzazioni_objects[0].dimensione_altezza,
          selectedLarghezza:
            nuovaPosizione.numero_colori?.[0]?.personalizzazioni_objects[0].dimensione_larghezza,
        }
      }
    } else {
      updated[index][key] = value
    }
    setPosizioni(updated)
  }

  const confezionamentoLabel = useMemo(() => {
    return posizioni[0]?.colori?.personalizzazioni_objects?.length > 0
      ? posizioni[0]?.colori?.personalizzazioni_objects[0].confezionamento
      : null
  }, [posizioni])

  const confezionamentoRichiestoOption = useMemo(() => {
    return posizioni[0]?.colori?.personalizzazioni_objects?.length > 0
      ? posizioni[0]?.colori?.personalizzazioni_objects[0].confezionamento_richiesto
      : null
  }, [posizioni])

  const prezzoConfezionamento = useMemo(() => {
    return posizioni[0]?.colori?.personalizzazioni_objects?.length > 0
      ? posizioni[0]?.colori?.personalizzazioni_objects[0].prezzo_confezionamento
      : null
  }, [posizioni])

  const articoliPreventivo = useMemo(() => {
    return quantita
      .map((q) => prodotto.articoli?.find((a) => a.codice === q.codice))
      ?.filter((x) => !!x)
  }, [prodotto, quantita])

  const getAreaStampa = (personalizzazione) => {
    return personalizzazione?.aree_stampa?.find(
      (a) =>
        a.colore?.id ===
        articoliPreventivo?.[0]?.attributi_lista?.find((attr) => attr.attributo_chiave === 'colore')
          .id
    )
  }

  // TODO: Da capire se necessario
  // useEffect(() => {
  //   confezionamentoRichiestoOption === 'D'
  //     ? ((confezionamento_richiesto = true), setConfezionamentoRichiesto(true))
  //     : ''

  //   confezionamentoRichiestoOption === 'N'
  //     ? ((confezionamento_richiesto = false), setConfezionamentoRichiesto(false))
  //     : ''

  //   confezionamentoRichiestoOption === 'S'
  //     ? ((confezionamento_richiesto = false), setConfezionamentoRichiesto(false))
  //     : ''
  // }, [])

  const [isValid, setIsValid] = useState(isFormValid || false)
  const [posizioniDuplicate, setPosizioniDuplicate] = useState(false)
  const [modalLogoOpen, setModalLogoOpen] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [personalizzazioneCarrello, setPersonalizzazioneCarrello] = useState(personalizzazioneId)

  useEffect(() => {
    if (!!setFormValid) {
      // se è passata come prop tengo allineato isValid con isFormValid
      setFormValid(isValid)
    }
  }, [isValid])

  useEffect(() => {
    const isFormikPartValid = formik.isValid // Check Yup validation

    if (!selectedTecnica) {
      setIsValid(false)
      return
    }

    if (!isFormikPartValid) {
      setIsValid(false)
      return false
    }

    if (posizioni.length > 0 && posizioni.every((pos) => pos.colori)) {
      // Verifica duplicati
      const ids = posizioni.map((pos) => pos.id)
      const hasDuplicates = new Set(ids).size !== ids.length
      setPosizioniDuplicate(hasDuplicates)
      setIsValid(!hasDuplicates)
      return
    }
    setPosizioniDuplicate(false)
    setIsValid(false)
  }, [selectedTecnica, posizioni, formik.isValid])

  const [posizioniFetch, setPosizioniFetch] = useState([])

  const aggiornaPreventivo = async (isPrenotazioneConfermata = false) => {
    trackingGAEventProdotto(
      'customization_funnel',
      'c_step1_posizione_stampa',
      undefined,
      first_articolo?.titolo || '',
      prodotto?.codice || '',
      selectedTecnica?.nome || ''
    )
    trackingGAEventProdotto(
      'customization_funnel',
      'c_step2_config_stampa_OLD',
      undefined,
      first_articolo?.titolo || '',
      prodotto?.codice || '',
      selectedTecnica?.nome || ''
    )
    trackingGAEventProdotto(
      'customization_funnel',
      confezionamentoRichiesto ? 'c_step3_confezionamento_si' : 'c_step3_confezionamento_no',
      undefined,
      first_articolo?.titolo || '',
      prodotto?.codice || '',
      selectedTecnica?.nome || ''
    )
    const params = {
      nome_lavorazione: formik.values.nomeLavorazione,
      confezionamento_richiesto: confezionamentoRichiesto,
      posizioni: posizioni.map((pos) => ({
        changedAltezza:
          pos.selectedAltezza !== pos.colori.personalizzazioni_objects[0].dimensione_altezza,
        changedLarghezza:
          pos.selectedLarghezza !== pos.colori.personalizzazioni_objects[0].dimensione_larghezza,
        ...(personalizzazioneCarrello && {
          altezza: pos.selectedAltezza,
          larghezza: pos.selectedLarghezza,
          pantoni_codici: pos.pantoni_codici,
          descrizione_posizione: pos.descrizione_posizione,
          logo_file: pos.logo_file_id,
          tracciatura_logo: pos.tracciatura_logo,
        }),
        personalizzazione_id: pos.colori.personalizzazioni_objects[0].id,
        personalizzazione: pos.colori.personalizzazioni_objects[0],
        // selectedColore: pos.numero_colori.find(n => n.codice === pos.colori.codice),
      })),
    }

    setLoading(true)
    try {
      const response = await (personalizzazioneCarrello
        ? api.put(`${PERSONALIZZAZIONICARRELLO}${personalizzazioneCarrello}/`, params)
        : api.post(`${PERSONALIZZAZIONICARRELLO}`, params))
      setPersonalizzazioneCarrello(response.data.id)

      const status = await AddToCart(quantita, response.data.id, isPrenotazioneConfermata, false)
      if (status) {
        // ritorna false se bisogna prima confermare la prenotazione
        setPosizioniFetch(response.data.posizioni)
        if (response.data.nome_lavorazione_manuale) {
          // se l'utente ha inserito il nome visualizzo sotto all'input il nome con eventuale incrementale
          setNomeLavorazioneAssegnato(response.data.nome_lavorazione)
        } else {
          setNomeLavorazioneAssegnato(null)
        }
        setAggiornaPreventivoEnabled(true)
        !isAlreadyAggiornaPreventivoEnabled &&
          !!setAlreadyAggiornaPreventivoEnabled &&
          setAlreadyAggiornaPreventivoEnabled(true) // diventa true dopo il primo aggiornamento del preventivo
        setShowPrenotazioneModal(false)
      } else {
        setShowPrenotazioneModal(!showPrenotazioneModal)
      }
    } catch (err) {
      Sentry.captureException(err)
      displayNotification('error.pers.edit', t('Qualcosa è andato storto'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const MakePayloadWithFiles = async (filesSelected, callback) => {
    if (filesSelected) {
      setLoading(true)
      // Prepara tutte le promesse di upload
      const uploadPromises = posizioniFetch.map(async (posizione, index) => {
        const posizioneData = posizioni[index]
        return _uploadLoghiDimensioni({
          id: posizione.id,
          file_to_upload: filesSelected[index],
          descrizione_posizione: posizioneData?.descrizione_posizione,
          altezza: posizioneData?.selectedAltezza,
          larghezza: posizioneData?.selectedLarghezza,
          pantoni_codici: posizioneData?.pantoni_codici,
          setLoading: setIsUploading,
        })
      })
      try {
        await Promise.all(uploadPromises)
        callback()
        setShowPersonalizzaForm(false)
        setLoading(false)
      } catch (err) {
        console.log(err.message)
        setLoading(false)
        displayNotification('upload.loghi.error', err.message, 'error')
      }
    }
  }

  useEffect(() => {
    if (modalLogoOpen) document.body.classList.add('w-noscroll')
    else document.body.classList.remove('w-noscroll')

    return () => document.body.classList.remove('w-noscroll')
  }, [modalLogoOpen])

  const getPrezzoColore = (tecnica, posizione, colore) => {
    const quantitaTotale = quantita.reduce((acc, curr) => acc + (curr.quantita || 0), 0)
    return quantitaTotale > 0
      ? prezziPersonalizzazioni?.tecniche
          ?.find((t) => t.id === tecnica.id)
          ?.posizioni.find((x) => x.id === posizione.id)
          ?.numero_colori.find((x) => x.id === colore.id)
          ?.personalizzazioni_objects[0].prezzi_personalizzazione.filter(
            (p) => quantitaTotale >= p.a_partire_da
          )
          .sort((a, b) => b.a_partire_da - a.a_partire_da)[0].prezzo
      : null
  }

  const resetConfirm = () => {
    if (isAggiornaPreventivoEnabled) {
      resetCarrello()
      setAggiornaPreventivoEnabled(false)
    }
  }

  // per triggerare l'azione da componente esterno
  useEffect(() => {
    if (needRecomputePreventivo) {
      resetConfirm()
      setNeedRecomputePreventivo(false)
    }
  }, [needRecomputePreventivo])

  // per triggerare l'azione da componente esterno
  useEffect(() => {
    if (doRecomputePreventivo) {
      aggiornaPreventivo()
      setDoRecomputePreventivo(false)
    }
  }, [doRecomputePreventivo])

  const [zoomModalIndex, setZoomModalIndex] = useState(null)

  const { width } = useWindowDimensions()

  return (
    <div className="configurazione-prodotto">
      {/* Tecnica di stampa */}
      <div className="configurazione-prodotto__item">
        <h2 className="configurazione-prodotto__title">
          {t('Seleziona la tecnica di stampa che desideri configurare')}
        </h2>
        <div className="configurazione-prodotto__tecniche">
          {tecniche && tecniche.length > 0 ? (
            tecniche.map((tecnica, index) => (
              <TecnicaCard
                key={index}
                tecnica={tecnica}
                selected={selectedTecnica?.id == tecnica?.id}
                setSelectedTecnica={(tecnica) => {
                  setSelectedTecnica(tecnica)
                  resetConfirm()
                  if (width < 767) {
                    handleScrollRef(document.getElementById('pos_0'))
                  }
                }}
                prezziTecnica={prezziPersonalizzazioni?.tecniche?.find((t) => t.id === tecnica.id)}
                quantita={quantita}
                consigliata={tecnica.consigliata}
                economica={tecnicaEconomica ? tecnicaEconomica.id === tecnica.id : false}
              />
            ))
          ) : (
            <p className="configurazione-prodotto__tecniche-message">
              {t('Non è disponibile nessuna tecnica di stampa per gli articoli selezionati')}
            </p>
          )}
        </div>
      </div>

      {/* Posizioni - lista */}
      {!selectedTecnica && <div id="pos_0" />}
      {selectedTecnica &&
        posizioni.map((pos, idx) => (
          <div className="configurazione-prodotto__item" id={'pos_' + idx} key={idx}>
            <h2 className="configurazione-prodotto__title">
              {t('Seleziona la posizione di stampa che desideri configurare')}
            </h2>
            <div className="configurazione-prodotto__posizioni" key={idx}>
              <div className="configurazione-prodotto__posizioni__head">
                <div className="configurazione-prodotto__posizioni__select">
                  <span className="configurazione-prodotto__posizioni__select__label">
                    {t('Scegli la posizione di stampa')}:
                  </span>
                  <FormField id={`selezione_posizione_${idx}`}>
                    <FormSelect
                      placeholder={t('Seleziona Posizione')}
                      options={
                        selectedTecnica?.posizioni
                          ?.filter(
                            (x) =>
                              posizioni?.[idx]?.id === x.id ||
                              !posizioni?.some((p, i) => i !== idx && p?.id === x.id)
                          )
                          ?.map((x) => ({
                            label: `${x.nome} - ${x?.numero_colori?.[0]?.personalizzazioni_objects?.[0].dimensione_larghezza} x ${x?.numero_colori?.[0]?.personalizzazioni_objects?.[0].dimensione_altezza} cm`,
                            value: x.id,
                          })) || []
                      }
                      value={posizioni?.[idx]?.id}
                      id={`select_pozione_${idx}`}
                      onChange={(opt) => {
                        handleChangePosizione(idx, 'posizione', opt.target.value)
                        resetConfirm()
                      }}
                    />

                    {posizioniDuplicate && (
                      <div className={'configurazione-prodotto__posizioni__select__error'}>
                        {t('Seleziona posizioni diverse per ogni configurazione.')}
                      </div>
                    )}
                  </FormField>
                </div>
                {selectedTecnica?.posizioni?.length > 1 && (
                  <div
                    className="configurazione-prodotto__posizioni__description"
                    dangerouslySetInnerHTML={{
                      __html: `<p><strong>* ${t('Attenzione')}:</strong> ${t(
                        'il numero massimo di posizioni di stampa selezionabili è 2.'
                      )}<br>${t(
                        '<strong>* Per personalizzazioni diverse</strong> da quelle mostrate contattare il servizio clienti.'
                      )}</p>`,
                    }}
                  />
                )}
              </div>
              <div className="configurazione-prodotto__posizioni__content">
                <div className="configurazione-prodotto__posizioni__colore">
                  <span className="configurazione-prodotto__posizioni__select__label">
                    {t('Scegli il tuo colore di stampa')}:
                  </span>
                  {pos.numero_colori.map((n, i) => (
                    <FormRadio
                      key={n.codice}
                      checked={pos.colori.codice === n.codice}
                      label={`${n.nome}${
                        n.full_color
                          ? ''
                          : n.numero_colori > 1
                          ? ` ${t('colori')}`
                          : ` ${t('colore')}`
                      } - ${priceNotation(getPrezzoColore(selectedTecnica, pos, n), 3)} € cad.`}
                      className={'configurazione-prodotto__posizioni__radio'}
                      size={'sm'}
                      color={'blue'}
                      onClick={() => {
                        handleChangePosizione(idx, 'colori', n)
                        resetConfirm()
                      }}
                    />
                  ))}
                </div>

                <div className="configurazione-prodotto__posizioni__dimensione">
                  <span className="configurazione-prodotto__posizioni__select__label">
                    {t('Dimensione di stampa')}:
                  </span>
                  <div className="configurazione-prodotto__posizioni__dimensione__value">
                    <div className="configurazione-prodotto__posizioni__dimensione__item ">
                      <Larghezza />
                      {`${
                        pos.numero_colori?.find((n) => pos.colori.codice === n.codice)
                          ?.personalizzazioni_objects?.[0]?.dimensione_larghezza
                      } cm`}
                    </div>
                    <div className="configurazione-prodotto__posizioni__dimensione__item">
                      <Altezza />
                      {`${
                        pos.numero_colori?.find((n) => pos.colori.codice === n.codice)
                          ?.personalizzazioni_objects?.[0]?.dimensione_altezza
                      } cm`}
                    </div>
                  </div>
                </div>
                {getAreaStampa(pos.numero_colori?.[0]?.personalizzazioni_objects?.[0])?.immagine
                  ?.url && (
                  <>
                    <NewModal
                      className="datatable-prodotto__modal"
                      visible={zoomModalIndex === idx}
                      onClose={() => setZoomModalIndex(null)}
                    >
                      <div className="main__image__wrapper">
                        <div className="main__image">
                          <MyImage
                            className=""
                            src={
                              getAreaStampa(pos.numero_colori?.[0]?.personalizzazioni_objects?.[0])
                                .immagine.url
                            }
                            alt={t('Immagine articolo con area di stampa')}
                            width={800}
                            height={800}
                            loading="eager"
                            onClick={() => setZoomModalIndex(null)}
                          />
                        </div>
                      </div>
                    </NewModal>
                    <div className="configurazione-prodotto__posizioni__imageBox">
                      <button
                        className={'configurazione-prodotto__posizioni__imageBox__zoom'}
                        onClick={() => setZoomModalIndex(idx)}
                      >
                        <Zoom />
                      </button>
                      <div className="configurazione-prodotto__posizioni__image">
                        <MyImage
                          src={
                            getAreaStampa(pos.numero_colori?.[0]?.personalizzazioni_objects?.[0])
                              .immagine.url
                          }
                          alt={t('Immagine articolo con area di stampa')}
                          width={400}
                          height={400}
                          objectFit="contain"
                          onClick={() => setZoomModalIndex(idx)}
                        />
                      </div>
                    </div>
                  </>
                )}
                {posizioni.length < 2 &&
                  idx === posizioni.length - 1 &&
                  selectedTecnica?.posizioni?.length > 1 && (
                    <Button
                      size="medium"
                      label={t("Aggiungi un'altra posizione di stampa")}
                      variant="secondary"
                      onClick={() => {
                        resetConfirm()
                        handleAggiungiPosizione()
                      }}
                      className={'configurazione-prodotto__posizioni__buttons'}
                      // disabled={isAggiornaPreventivoEnabled}
                    />
                  )}
                {posizioni.length === 2 && (
                  <Button
                    size="medium"
                    label={t('Rimuovi posizione di stampa')}
                    variant="secondary"
                    colorVariant="orange"
                    onClick={() => {
                      resetConfirm()
                      handleRimuoviPosizione(idx)
                      idx > 0 && handleScrollRef(document.getElementById('pos_' + 0))
                    }}
                    className={
                      'configurazione-prodotto__posizioni__buttons configurazione-prodotto__posizioni__buttons__rimuovi'
                    }
                    // disabled={isAggiornaPreventivoEnabled}
                  />
                )}
              </div>
            </div>
          </div>
        ))}

      {/* Servizi aggiuntivi */}
      {selectedTecnica && (
        <div className="configurazione-prodotto__item">
          <h2 className="configurazione-prodotto__title">{t('Seleziona i servizi aggiuntivi')}</h2>

          <p className="configurazione-prodotto__description">
            {t('Seleziona il confezionamento disponibile per il tuo prodotto')}
          </p>
          {confezionamentoRichiestoOption === 'D' ? null : confezionamentoRichiestoOption ===
            'N' ? null : (
            <div>
              <FormCheckbox
                label={
                  t('Richiedi Confezionamento per') +
                  ' ' +
                  `${priceNotation(prezzoConfezionamento, 3)} €`
                }
                type="checkbox"
                checked={confezionamentoRichiesto}
                onChange={(e) => {
                  setConfezionamentoRichiesto(e.target.checked ? true : false)
                  resetConfirm()
                }}
                color={'blue'}
                className={'configurazione-prodotto__posizioni__checkbox'}
                // disabled={isAggiornaPreventivoEnabled}
              />
            </div>
          )}
          {confezionamentoRichiestoOption === 'D' && (
            <div className="confezionamento__stampa__content">
              <FormCheckbox
                label={`${confezionamentoLabel} (${t('senza costi aggiuntivi')})`}
                type="checkbox"
                checked={true}
                color={'blue'}
                className={'configurazione-prodotto__posizioni__checkbox'}
                disabled={true}
              />
            </div>
          )}
          {confezionamentoRichiestoOption === 'N' && (
            <div className="confezionamento__stampa__content">
              <FormCheckbox
                label={`${confezionamentoLabel} (${t('senza costi aggiuntivi')})`}
                type="checkbox"
                checked={true}
                color={'blue'}
                className={'configurazione-prodotto__posizioni__checkbox'}
                disabled={true}
              />
            </div>
          )}

          <p className="configurazione-prodotto__description configurazione-prodotto__description__lavorazione">
            {t(
              'Inserisci il nome lavorazione, se non specificato, verrà creato un identificativo automatico'
            )}
          </p>
          <FormField
            id={`nome_lavorazione`}
            status={
              formik.touched.nomeLavorazione && formik.errors.nomeLavorazione ? 'error' : undefined
            }
            errorMessage={formik.errors.nomeLavorazione ? formik.errors.nomeLavorazione : undefined}
            infoMessage={nomeLavorazioneAssegnato}
          >
            <FormInput
              name="nomeLavorazione"
              value={formik.values.nomeLavorazione}
              placeholder={t('Nome lavorazione (opzionale)')}
              type="text"
              onChange={(e) => {
                resetConfirm()
                formik.handleChange(e)
              }}
              onBlur={formik.handleBlur}
            />
          </FormField>
        </div>
      )}

      {/* Conferma */}
      {selectedTecnica && posizioni.length > 0 && (
        <div className="configurazione-prodotto__item" id="configurazione-prodotto__buttons">
          <h2 className="configurazione-prodotto__title">
            {t('Aggiorna il preventivatore e inviaci la grafica')}
          </h2>
          <p className="configurazione-prodotto__description">
            {t('Aggiorna il prezzo e poi seleziona come preferisci inviarci il file per la stampa')}
          </p>
          <div className="configurazione-prodotto__buttons">
            {isAggiornaPreventivoEnabled && (
              <>
                <Button
                  size="medium"
                  label={t('Carica il logo e aggiungi al carrello')}
                  variant={'primary'}
                  onClick={() => setModalLogoOpen(true)}
                  disabled={!isValid || isUploading || isLoading}
                  className={'configurazione-prodotto__buttons__submit'}
                  icon={<Clipboard />}
                  icon_pos={'left'}
                />
                <Tooltip
                  label={'Coming soon'}
                  containerClassName={'configurazione-prodotto__buttons__tooltip'}
                >
                  <Button
                    size="medium"
                    label={t('Crea la tua bozza con il nostro editor')}
                    variant="primary-gradient"
                    icon={<Customize />}
                    icon_pos="left"
                    disabled={true}
                    // disabled={!isValid || isUploading}
                    className={'configurazione-prodotto__buttons__comingsoon'}
                  />
                </Tooltip>
              </>
            )}
            {!isAggiornaPreventivoEnabled && (
              <Button
                size="medium"
                label={t('Aggiorna il preventivatore e carica il tuo logo')}
                variant="primary-gradient"
                onClick={() => {
                  aggiornaPreventivo()
                }}
                disabled={!isValid || isUploading || isLoading}
                className={'configurazione-prodotto__buttons__confirm'}
              />
            )}
          </div>
        </div>
      )}

      {(!isEdit || (isEdit && !isAggiornaPreventivoEnabled)) && (
        <Button
          label={t('Annulla personalizzazione')}
          onClick={() => setShowAnnullaConfigurazioneModal(true)}
          variant="ghost"
          colorVariant="orange"
        />
      )}
      <NewModal
        visible={modalLogoOpen}
        closeOnOverlayClick={false}
        onClose={() => setModalLogoOpen(false)}
        title={t('Caricamento loghi')}
        className={'configurazione-prodotto__modal'}
      >
        <ConfigurazioneProdottoConferma
          articoliPreventivo={articoliPreventivo}
          onConfirm={(filesSelected) => {
            setModalLogoOpen(false)
            MakePayloadWithFiles(filesSelected, AddToCartMain)
            trackFinalStep(first_articolo, prodotto, selectedTecnica)
            trackingAddCart(ordine, 'standard_order')
          }}
          onCreaOfferta={(filesSelected) => {
            setModalLogoOpen(false)
            MakePayloadWithFiles(filesSelected, () => {})
            trackFinalStep(first_articolo, prodotto, selectedTecnica)
            trackingCreaOfferta(ordine)
            router?.push(`/crea-offerta?cart=${ordine?.id}`)
          }}
          onSaveLater={
            !!SaveLater
              ? (filesSelected) => {
                  setModalLogoOpen(false)
                  MakePayloadWithFiles(filesSelected, SaveLater)
                  trackFinalStep(first_articolo, prodotto, selectedTecnica)
                  trackingSalvaPerDopo(ordine)
                }
              : undefined
          }
          handleChange={handleChangePosizione}
          stessoLogo={posizioni.length === 1}
          posizioni={posizioni}
        />
      </NewModal>
      <NewModal
        alert={true}
        visible={showAnnullaConfigurazioneModal}
        onClose={() => setShowAnnullaConfigurazioneModal(false)}
        title={t('Sei sicuro di voler annullare la personalizzazione?')}
        text={t("Cliccando su 'Conferma e procedi' perderai la configurazione creata.")}
        actions={[
          <Button
            label={t('Annulla e torna indietro')}
            variant="primary"
            size="medium"
            onClick={() => {
              trackingGAEventProdotto(
                'customization_delete',
                'annulla',
                undefined,
                first_articolo?.titolo || '',
                prodotto?.codice || ''
              )
              setShowAnnullaConfigurazioneModal(false)
            }}
          />,
          <Button
            label={t('Conferma e procedi')}
            variant="secondary"
            size="medium"
            onClick={() => {
              setShowAnnullaConfigurazioneModal(false)
              variant === 'personalizzazione'
                ? router.push('/cart/')
                : setShowPersonalizzaForm(false)
              trackingGAEventProdotto(
                'customization_delete',
                'conferma_e_procedi',
                undefined,
                first_articolo?.titolo || '',
                prodotto?.codice || ''
              )
            }}
          />,
        ]}
      />
      {quantita?.length > 0 && (
        <NewModal
          visible={showPrenotazioneModal}
          alert={true}
          title={t('La quantità inserita non è disponibile, desideri prenotarla?')}
          text={
            <>
              <p>
                {t(
                  'La quantita inserita non è disponibile subito, ma è possibile prenotarla su più arrivi.'
                )}
                <br />
                {t(
                  "L'ordine verrà evaso dal prossimo arrivo della merce che soddisfa le quantità richieste."
                )}
              </p>
              <p>{t('Se vuoi ricevere subito gli articoli, inserisci una quantità disponibile')}</p>
            </>
          }
          className={'prenota-modal'}
          onClose={() => setShowPrenotazioneModal(false)}
          actions={[
            <Button
              label={t('Modifica quantità')}
              variant="secondary"
              size="medium"
              onClick={() => setShowPrenotazioneModal(false)}
            />,
            <Button
              label={t('Conferma prenotazione')}
              variant="primary"
              size="medium"
              onClick={() => {
                aggiornaPreventivo(true)
              }}
            />,
          ]}
        >
          <ModalBlocks.ProssimiArrivi
            articoli={quantita?.filter((a) => a.prenotazione && a.quantita > 0)}
          />
        </NewModal>
      )}
    </div>
  )
}

export default ConfigurazioneProdotto
